<template>
  <v-skeleton-loader
    :loading="skeleton"
    :class="{ 'is-disabled': disabled }"
    type="card-avatar"
    class="BEditor"
  >
    <v-input
      :error="$errors.has(name)"
      :error-messages="$errors.first(name)"
      hide-details="auto"
    >
      <v-card class="BEditor__card" outlined :color="$errors.has(name) ? 'error' : ''">
        <v-row no-gutters>
          <v-col :cols="actionsLeftSide ? 'auto' : 12">
            <div v-if="extensions.length" class="secondary pa-1 full-height">
              <EditorMenuBar v-slot="props" :editor="editor">
                <BEditorActions
                  v-bind="props"
                  :editor="editor"
                  :code-view-mode="isCodeViewMode"
                  :disabled="disabled"
                  @mode:toggle="onToggleMode"
                />
              </EditorMenuBar>
            </div>
          </v-col>

          <v-col :cols="actionsLeftSide ? null : 12">
            <div v-if="isCodeViewMode">
              <textarea ref="cmTextArea" />
            </div>
            <BEditorContent
              v-show="!isCodeViewMode"
              v-validate="validate"
              :value="value"
              :data-vv-as="`«${dataVvAs}»`"
              :editor="editor"
              :style="{ minHeight }"
              :name="name"
              :class="{ 'is-dense': dense }"
              class="BEditor__content"
            />
          </v-col>
        </v-row>
      </v-card>
    </v-input>
  </v-skeleton-loader>
</template>

<script>
/**
 * https://iliyazelenko.github.io/tiptap-vuetify-demo
 * https://tiptap.dev
 */

import {
  Editor,
  EditorMenuBar,
} from 'tiptap'
import {
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  CodeBlock,
  // Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
  Table,
  TableHeader,
  TableCell,
  TableRow,
  Image,
  Placeholder,
} from 'tiptap-extensions'
import uniqueId from 'lodash/uniqueId'
import CodeViewMixin from './BEditor/CustomExtensions/CodeView/CodeViewMixin'

import CodeView from './BEditor/CustomExtensions/CodeView'
import { Alignment } from './BEditor/Alignment'
import { Link } from './BEditor/Link'
import { Color } from './BEditor/Color'
import BEditorActions from './BEditor/BEditorActions.vue'
import BEditorContent from './BEditor/BEditorContent.vue'


export {
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  CodeBlock,
  // Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
  Table,
  TableHeader,
  TableCell,
  TableRow,
  Image,
} from 'tiptap-extensions'
export { Alignment } from './BEditor/Alignment'
export { Color } from './BEditor/Color'
export { Link } from './BEditor/Link'
export { ExcelCell } from './BEditor/ExcelCell'


const EXTENSIONS = [
  new History(),

  new Bold(),
  new Italic(),
  new Underline(),
  new Strike(),

  new Alignment(),
  new Heading(),

  new Color(),
  // Color и ColorBg конфликтуют между собой
  // new ColorBg(),
  new Blockquote(),

  new Table({
    resizable: true,
    lastColumnResizable: true,
  }),
  new TableHeader(),
  new TableCell(),
  new TableRow(),

  new ListItem(),
  new BulletList(),
  new OrderedList(),

  new HorizontalRule(),
  new HardBreak(),
  new Code(),
  new CodeBlock(),
  new Link(),
  new Image(),
  new Placeholder({
    emptyNodeText: null,
    showOnlyWhenEditable: true,
    showOnlyCurrent: true,
  }),
  new CodeView(),
]

export default {
  name: 'BEditor',
  inject: ['$validator'],
  components: {
    EditorMenuBar,
    BEditorActions,
    BEditorContent,
  },
  mixins: [
    CodeViewMixin,
  ],
  inheritAttrs: false,
  props: {
    skeleton: Boolean,
    disabled: Boolean,
    placeholder: String,
    actionsLeftSide: Boolean,
    dense: Boolean,
    editorProps: {
      type: Object,
      default: () => ({}),
    },
    validate: {
      type: [String, Object],
    },
    name: {
      type: String,
      default: () => uniqueId('b-editor-'),
    },
    dataVvAs: String,
    value: {
      type: String,
    },
    extensions: {
      type: Array,
      default: () => EXTENSIONS,
    },
    minHeight: String,
  },
  data: () => ({
    editor: null,
    html: '',
  }),
  watch: {
    disabled(value) {
      this.editor?.setOptions({ editable: !value })
    },
    value: {
      handler(value) {
        const { editor } = this
        if (value === this.html || !editor) return
        const { from } = editor.selection
        editor.setContent(value)
        editor.setSelection(from, from)
      },
    },
  },
  mounted() {
    this.editor = new Editor({
      editorProps: this.editorProps,
      editable: !this.disabled,
      content: this.value,
      extensions: this.extensions,
      onUpdate: this.onUpdate,
    })

    const { placeholder } = this.editor.extensions.options
    if (placeholder && this.placeholder) {
      placeholder.emptyNodeText = this.placeholder
    }
  },
  beforeDestroy() {
    this.editor?.destroy()
  },
  methods: {
    onUpdate() {
      // let html = this.editor.getHTML().replace('/n', '###')
      let html = this.editor.getHTML()
      // если в содержимом только теги, подставляемые редактором, то обрабатывать как пустой контент
      if (html === '<p></p>') html = ''
      this.html = html
      this.$emit('input', html)
    },
    onToggleMode(v) {
      this.isCodeViewMode = v
    },
  },
}
</script>

<style lang="scss">
.BEditor {
  .ProseMirror {
    min-height: inherit;
    padding: 16px;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.87) !important;

    pre code {
      display: block;
      color: $color-secondary;
      background: $color-black;
    }

    p code {
      color: $color-black;
      background: $color-secondary;
      border-radius: 5px;
    }

    a {
      pointer-events: none;
    }
  }

  &__content {
    &.is-dense {
      .ProseMirror {
        padding: 7px 12px;
      }
    }
  }

  img {
    max-width: 100%;
  }

  p:last-child {
    margin-bottom: 0;
  }

  table {
    width: 100%;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;
    border-collapse: collapse;

    td,
    th {
      position: relative;
      -webkit-box-sizing: border-box;
      box-sizing: border-box;
      min-width: 1em;
      padding: 3px 5px;
      vertical-align: top;
      border: 2px solid $color-gray;
    }

    td > *,
    th > * {
      margin-bottom: 0;
    }

    th {
      font-weight: 700;
      text-align: left;
    }

    .selectedCell::after {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 2;
      pointer-events: none;
      content: "";
      background: rgba($color-info, 0.1);
    }

    .column-resize-handle {
      position: absolute;
      top: 0;
      right: -2px;
      bottom: 0;
      z-index: 20;
      width: 4px;
      pointer-events: none;
      background-color: $color-info;
    }
  }

  .is-editor-empty:first-child::before {
    float: left;
    height: 0;
    color: $color-gray;
    pointer-events: none;
    content: attr(data-empty-text);
  }

  .error .is-editor-empty:first-child::before {
    color: $color-error;
  }

  .tableWrapper {
    margin: 1em 0;
    overflow-x: auto;
  }

  &.is-disabled .ProseMirror {
    outline-color: transparent;
  }

  &__card {
    width: 100%;

    &.error {
      background-color: transparent !important;
    }
  }
}
</style>
